import { httpClient as request } from '@/utils/http'
import type { AxiosRequestConfig } from 'axios'

const railwayStationSilentErrorConfig: AxiosRequestConfig & { showErrorMessage: false } = {
  showErrorMessage: false
}

export enum RailwayStationEnabledStatus {
  DISABLED = 0,
  ENABLED = 1
}

export type RailwayStationId = string | number

export interface RailwayStationContainerTypeVO {
  id: RailwayStationId
  containerTypeCode?: string
  isoCode?: string
  description?: string
  enabled?: number
}

export interface RailwayStationVO {
  id: RailwayStationId
  tenantId?: RailwayStationId
  stationCode: string
  nameCn: string
  nameEn?: string
  aliasName?: string
  railwayCarrierId: RailwayStationId
  railwayCarrierNameCn?: string
  railwayCarrierNameEn?: string
  railwayCarrierCode?: string
  countryId: RailwayStationId
  countryName?: string
  countryNameEn?: string
  provinceId?: RailwayStationId
  provinceName?: string
  provinceNameEn?: string
  railwayLineName?: string
  belongingChannel?: string
  trackGauge?: string
  stationType: string
  stationTypeName?: string
  operationStatus: string
  operationStatusName?: string
  operationDirection: string
  operationDirectionName?: string
  borderChannelType?: string
  borderChannelTypeName?: string
  gaugeChangeFlag?: number
  gaugeChangeCount?: number
  foreignStationName?: string
  foreignUicCode?: string
  customsSupervisionCode?: string
  longitude?: number | string
  latitude?: number | string
  maxLoadLimit?: number | string
  enabled: number
  version?: number
  changeRemark?: string
  containerTypeIds?: RailwayStationId[]
  containerTypes?: RailwayStationContainerTypeVO[]
  dateCreated?: string
  dateModified?: string
}

export interface RailwayStationQueryRequest {
  pageNum?: number
  pageSize?: number
  keyword?: string
  stationCode?: string
  nameCn?: string
  railwayCarrierId?: RailwayStationId
  countryId?: RailwayStationId
  provinceId?: RailwayStationId
  trackGauge?: string
  stationType?: string
  operationStatus?: string
  operationDirection?: string
  borderChannelType?: string
  enabled?: number
  ids?: RailwayStationId[]
}

export interface PageResponse<T> {
  pageNum: number
  pageSize: number
  total: number
  records: T[]
}

const BASE_URL = '/mdm/railwayStation'

export class RailwayStationApi {
  static queryPage(params: RailwayStationQueryRequest): Promise<PageResponse<RailwayStationVO>> {
    return request.post(`${BASE_URL}/queryPage`, params)
  }

  static queryList(params: RailwayStationQueryRequest): Promise<RailwayStationVO[]> {
    return request.post(`${BASE_URL}/queryList`, params)
  }

  static queryPlatformPage(params: RailwayStationQueryRequest): Promise<PageResponse<RailwayStationVO>> {
    return request.post(`${BASE_URL}/queryPlatformPage`, params)
  }

  static queryPlatformList(params: RailwayStationQueryRequest): Promise<RailwayStationVO[]> {
    return request.post(`${BASE_URL}/queryPlatformList`, params)
  }

  static getById(id: RailwayStationId): Promise<RailwayStationVO> {
    return request.post(`${BASE_URL}/getById`, { id })
  }

  static updateTenantStatus(ids: RailwayStationId[], enabled: RailwayStationEnabledStatus): Promise<boolean> {
    return request.post(`${BASE_URL}/updateTenantStatus`, { ids, enabled })
  }

  static batchAssociate(platformIds: RailwayStationId[]): Promise<boolean> {
    return request.post(`${BASE_URL}/batchAssociate`, { platformIds }, railwayStationSilentErrorConfig)
  }

  static batchDisassociate(ids: RailwayStationId[]): Promise<boolean> {
    return request.post(`${BASE_URL}/batchDisassociate`, { ids })
  }

  static options(params: RailwayStationQueryRequest = {}): Promise<RailwayStationVO[]> {
    return request.post(`${BASE_URL}/options`, params)
  }
}

export default RailwayStationApi
